import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, LoginCredentials } from './types'
import { authService } from './services/auth.service'
import { useAccountContextStore } from '@/shared/stores/accountContext'
import { useResellerContextStore } from '@/shared/stores/resellerContext'
import { usePortalBrandingStore } from '@/shared/stores/portalBranding'
import { useUndoStore } from '@/shared/stores/undo'

const branding = () => usePortalBrandingStore()

/** Auth session: login, logout, impersonation, token, and user profile. */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const isImpersonating = ref<boolean>(!!localStorage.getItem('original_auth_token'))
  const isLoggingOut = ref<boolean>(false)
  const isAuthenticated = computed(() => !!token.value)

  /** Clear undo banners and timers (e.g. on login, impersonation, logout). */
  const clearUndoState = () => {
    useUndoStore().clear()
  }

  /** Reset account/reseller working context (e.g. on impersonation). */
  const clearScopedContext = () => {
    useResellerContextStore().clear()
    useAccountContextStore().clear()
  }

  // Authenticates user and stores token + profile in state and localStorage.
  async function login(credentials: LoginCredentials) {
    try {
      const response = await authService.login(credentials)

      if (response.result === 'success' && response.token) {
        token.value = response.token
        user.value = response.user[0] || null

        localStorage.setItem('auth_token', response.token)
        localStorage.setItem('user', JSON.stringify(user?.value || {}))

        branding().applyFromUser(user.value)

        clearUndoState()

        void import('@/shared/realtime/echo').then(({ resetEcho }) => resetEcho())

        return response
      }

      throw new Error('Invalid login response')
    } catch (error) {
      console.log(error)
      throw error
    }
  }

  // Ends session, clears storage, undo state, and reseller context.
  async function logout() {
    if (isLoggingOut.value) return
    isLoggingOut.value = true
    try {
      // End impersonation on the server before clearing local session.
      if (isImpersonating.value) {
        try {
          await authService.leaveImpersonation()
          const originalToken = localStorage.getItem('original_auth_token')
          if (originalToken) {
            token.value = originalToken
          }
        } catch (e) {
          console.warn('Failed to leave impersonation during logout', e)
        }
      }
      await authService.logout()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      user.value = null
      token.value = null
      isImpersonating.value = false
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
      localStorage.removeItem('original_auth_token')
      localStorage.removeItem('original_user')

      clearUndoState()
      useResellerContextStore().clear()
      useAccountContextStore().clear()
      try {
        const { clearMessagingChannelBus } = await import('@/shared/realtime/messagingChannelBus')
        clearMessagingChannelBus()
      } catch {
        // ignore
      }
      const { disconnectEcho } = await import('@/shared/realtime/echo')
      disconnectEcho()
      void import('@/shared/stores/messagingNotifications').then(
        ({ useMessagingNotificationsStore }) => {
          useMessagingNotificationsStore().resetForSessionChange()
        }
      )
      try {
        await branding().onLogout()
      } finally {
        isLoggingOut.value = false
      }
    }
  }

  // Clears session locally without calling API.
  function clearSession() {
    user.value = null
    token.value = null
    isImpersonating.value = false
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
    localStorage.removeItem('original_auth_token')
    localStorage.removeItem('original_user')

    clearUndoState()
    useResellerContextStore().clear()
    useAccountContextStore().clear()
    void import('@/shared/realtime/messagingChannelBus').then(({ clearMessagingChannelBus }) => {
      clearMessagingChannelBus()
      void import('@/shared/realtime/echo').then(({ disconnectEcho }) => disconnectEcho())
    })
    void import('@/shared/stores/messagingNotifications').then(
      ({ useMessagingNotificationsStore }) => {
        useMessagingNotificationsStore().resetForSessionChange()
      }
    )
    void branding().onLogout()
  }

  // Hydrates user and token from localStorage on app startup.
  function initAuth() {
    const savedToken = localStorage.getItem('auth_token')
    const savedUser = localStorage.getItem('user')

    if (savedToken) {
      token.value = savedToken
    }
    if (savedUser) {
      try {
        user.value = JSON.parse(savedUser)
      } catch (e) {
        console.error('Failed to parse saved user', e)
      }
    }
  }

  /** Tear down Echo + messaging bus, then rebuild with the current auth_token. */
  async function rebindRealtimeSession() {
    try {
      const { clearMessagingChannelBus } = await import('@/shared/realtime/messagingChannelBus')
      clearMessagingChannelBus()
    } catch {
      // ignore
    }
    try {
      const { resetEcho } = await import('@/shared/realtime/echo')
      resetEcho()
    } catch {
      // ignore
    }
    try {
      const { useMessagingNotificationsStore } =
        await import('@/shared/stores/messagingNotifications')
      useMessagingNotificationsStore().resetForSessionChange()
    } catch {
      // ignore
    }
  }

  // Switches session to another user while preserving the original session.
  async function impersonate(userId: number | string) {
    try {
      const response = await authService.impersonate(userId)

      if (response.result === 'success' && response.token) {
        // Stash admin token/user once so leaveImpersonation can restore them.
        if (!isImpersonating.value) {
          localStorage.setItem('original_auth_token', token.value || '')
          localStorage.setItem('original_user', JSON.stringify(user.value || {}))
        }

        // Drop old Echo subscriptions before swapping identity.
        try {
          const { clearMessagingChannelBus } = await import('@/shared/realtime/messagingChannelBus')
          clearMessagingChannelBus()
          const { disconnectEcho } = await import('@/shared/realtime/echo')
          disconnectEcho()
        } catch {
          // ignore
        }

        token.value = response.token
        user.value = response.user[0] || null
        isImpersonating.value = true

        localStorage.setItem('auth_token', response.token)
        localStorage.setItem('user', JSON.stringify(user.value || {}))

        branding().applyFromUser(user.value)

        clearUndoState()
        clearScopedContext()

        await rebindRealtimeSession()

        return response
      }

      throw new Error('Invalid impersonation response')
    } catch (error) {
      console.error('Impersonation failed:', error)
      throw error
    }
  }

  // Restores the original admin session after impersonation.
  async function leaveImpersonation() {
    const restoreLocalSession = async (originalToken: string, originalUserRaw: string) => {
      try {
        const { clearMessagingChannelBus } = await import('@/shared/realtime/messagingChannelBus')
        clearMessagingChannelBus()
        const { disconnectEcho } = await import('@/shared/realtime/echo')
        disconnectEcho()
      } catch {
        // ignore
      }

      token.value = originalToken
      user.value = JSON.parse(originalUserRaw)
      isImpersonating.value = false

      localStorage.setItem('auth_token', originalToken)
      localStorage.setItem('user', originalUserRaw)
      localStorage.removeItem('original_auth_token')
      localStorage.removeItem('original_user')

      branding().applyFromUser(user.value)
      clearUndoState()
      clearScopedContext()
      await rebindRealtimeSession()
    }

    try {
      await authService.leaveImpersonation()

      const originalToken = localStorage.getItem('original_auth_token')
      const originalUser = localStorage.getItem('original_user')

      if (originalToken && originalUser) {
        await restoreLocalSession(originalToken, originalUser)
      } else {
        await logout()
      }
    } catch (error) {
      console.error('Leave impersonation failed:', error)
      throw error
    }
  }

  // Refreshes the current user from the API and updates localStorage.
  async function fetchProfile() {
    try {
      const response = await authService.getProfile()
      if (response.result === 'success' && response.user) {
        user.value = response.user[0] || null
        localStorage.setItem('user', JSON.stringify(user.value || {}))
        branding().applyFromUser(user.value)
        return response
      }
      throw new Error('Invalid profile response')
    } catch (error) {
      console.error('Failed to fetch profile:', error)
      throw error
    }
  }

  return {
    user,
    token,
    isImpersonating,
    isLoggingOut,
    isAuthenticated,
    login,
    logout,
    impersonate,
    leaveImpersonation,
    initAuth,
    fetchProfile,
    clearSession,
    // Updates in-memory token and persists to localStorage.
    setToken: (newToken: string) => {
      token.value = newToken
      localStorage.setItem('auth_token', newToken)
    }
  }
})
